import {
  Box,
  measureText,
  OverflowTooltip,
  Stack,
  styled,
  Typography,
  useTheme,
  useTypographyVariantFontForMeasuringText,
} from '@chronosphereio/chrono-ui';
import { getFormattedDateTimeString, scaleLinear, useFeatureFlags } from '@chronosphereio/core';
import { useCallback, useMemo, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import {
  CrosshairLine,
  DEFAULT_NO_DATA_MESSAGE,
  PANEL_TOOLTIP_MAX_WIDTH,
  PANEL_TOOLTIP_MIN_WIDTH,
  PanelTooltipWrapper,
  TooltipLink,
  useChartsTheme,
  usePanelCrosshair,
  useTimeZone,
} from '@/components';
import { EVENT_TIMELINE_HEIGHT, EventTimeline } from '@/components/EventTimeline';
import { SeriesTooltipActions } from '@/components/TimeSeriesTooltip/SeriesTooltipActions';
import {
  DEFAULT_VISUAL_DENSITY,
  formatValue,
  getCommonTimeScale,
  getTimeSeriesValues,
  QueryDefinition,
  resolveSyncGroup,
  TimeSeriesDataResponse,
  TimeSeriesValueTuple,
  UnitOptions,
} from '@/core';
import { mergeTimeSeries, useAppMetadataContext, useEventContext } from '@/plugin-system';
import { useTimeRange } from '@/plugin-system/runtime/TimeRangeProviderV2';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';
import { Axis } from './Axis';
import { SelectableOverlay } from './SelectableOverlay';
import { StaticLegend, StaticLegendItem } from './StaticLegend';
import {
  AXIS_PLUS_LEGEND_HEIGHT,
  ESTIMATED_CHAR_WIDTH_PX,
  LABEL_PADDING,
  MAX_LABEL_WIDTH_FRACTION,
  ROW_LABEL_WIDTH,
  ROW_SPACING,
  STATUS_CELL_BORDER_WIDTH,
  StatusHistoryProps,
  statusHistoryTimestampToPixelX,
} from './status-history-models';
import { TimeRangeHoverPointer } from './TimeRangeHoverPointer';
import {
  SeriesWithLabels,
  useGroupedSeries,
  useResolvedLabelColorMappings,
  useStatusHistoryTooltipLinks,
} from './utils';

const MIN_CHART_HEIGHT = 100;

function useLabelWidth(series: Array<{ formattedName?: string; name: string }> | undefined, panelWidth: number) {
  const captionFont = useTypographyVariantFontForMeasuringText('caption');

  return useMemo(() => {
    const maxFraction = Math.floor(panelWidth * MAX_LABEL_WIDTH_FRACTION);
    if (!series?.length) return maxFraction;

    let maxTextWidth = 0;
    for (const s of series) {
      const label = s.formattedName ?? s.name;
      const metrics = measureText(label, captionFont);
      if (metrics && metrics.width > maxTextWidth) {
        maxTextWidth = metrics.width;
      }
    }

    if (maxTextWidth === 0) {
      const maxChars = series.reduce((m, s) => Math.max(m, (s.formattedName ?? s.name).length), 0);
      maxTextWidth = maxChars * ESTIMATED_CHAR_WIDTH_PX;
    }

    const contentWidth = Math.ceil(maxTextWidth) + LABEL_PADDING;
    return Math.max(ROW_LABEL_WIDTH, Math.min(contentWidth, maxFraction));
  }, [series, panelWidth, captionFont]);
}

interface TooltipContent {
  name: string;
  timestamp: number;
  value: number | null;
  formattedValue: string;
  status: string | undefined;
  statusColor: string | undefined;
  rowIdx: number;
  labels?: Record<string, string>;
}

const StatusCell = styled('div', {
  shouldForwardProp: (prop) => prop !== 'backgroundColor' && prop !== 'height',
})<{ backgroundColor?: string; height: number }>(({ theme, backgroundColor, height }) => ({
  borderRight: `${STATUS_CELL_BORDER_WIDTH}px solid ${theme.palette.background.container}`,
  borderBottom: `1px solid ${theme.palette.background.container}`,
  backgroundColor,
  height: `${height}px`,
  minWidth: '1px',
  flex: 1,
  cursor: 'pointer',
}));

const RowContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'opacity',
})<{ opacity?: number }>(({ opacity }) => ({
  alignItems: 'center',
  opacity: opacity ?? 1,
  transition: 'opacity 250ms ease',
}));

const RowLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'height' && prop !== 'labelWidth',
})<{ height: number; labelWidth: number }>(({ height, labelWidth, theme }) => ({
  minWidth: `${labelWidth}px`,
  maxWidth: `${labelWidth}px`,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textAlign: 'right',
  flex: 0,
  userSelect: 'none',
  height: `${height}px`,
  lineHeight: `${height}px`,
  borderRight: `${theme.spacing(1)} solid transparent`,
  display: 'block',
}));

const LabelContent = styled('span')({
  userSelect: 'none',
});

const CellsContainer = styled(Stack)({
  flex: 1,
});

interface StatusHistoryRowProps {
  name: string;
  labels?: Record<string, string>;
  values: TimeSeriesValueTuple[];
  getStatus: (value: number | null) => { name?: string; color?: string };
  getStatusFromLabel?: (label: string | undefined) => { name?: string; color?: string };
  cellStates?: Array<string | undefined>;
  setTooltipContent: (content?: TooltipContent) => void;
  unit?: UnitOptions;
  rowIdx: number;
  selectedRowIdx: number | undefined;
  setHoveredColIdx: (colIdx: number | undefined) => void;
  onCellHover?: (timestampMs: number | undefined) => void;
  height: number;
  labelWidth: number;
}

const StatusHistoryRow = ({
  name,
  labels,
  getStatus,
  getStatusFromLabel,
  cellStates,
  values,
  setTooltipContent,
  unit,
  rowIdx,
  selectedRowIdx,
  setHoveredColIdx,
  onCellHover,
  height,
  labelWidth,
}: StatusHistoryRowProps) => {
  const getTargetElement = useCallback(() => document.querySelector(`[data-overflowid='${name}']`), [name]);
  const rowOpacity = selectedRowIdx !== undefined && selectedRowIdx !== rowIdx ? 0.8 : 1;
  const isColorByLabelActive = cellStates !== undefined && getStatusFromLabel !== undefined;

  return (
    <RowContainer direction="row" opacity={rowOpacity}>
      <RowLabel
        data-overflowid={name}
        variant="caption"
        height={height}
        labelWidth={labelWidth}
        onClick={(e) => e.stopPropagation()}
      >
        <OverflowTooltip getTargetElement={getTargetElement} Content={name}>
          <LabelContent>{name}</LabelContent>
        </OverflowTooltip>
      </RowLabel>
      <CellsContainer
        direction="row"
        draggable={false}
        onMouseLeave={() => {
          // Cursor left the cells area (e.g. moved to row labels) — clear stale crosshair.
          onCellHover?.(undefined);
        }}
      >
        {values.map((value, idx) => {
          const status = isColorByLabelActive ? getStatusFromLabel(cellStates[idx]) : getStatus(value?.[1]);
          return (
            <StatusCell
              key={idx}
              aria-label={status.name}
              draggable={false}
              backgroundColor={status.color}
              height={height}
              onMouseEnter={() => {
                setHoveredColIdx(idx);
                onCellHover?.(value[0]);
                let formattedValue = DEFAULT_NO_DATA_MESSAGE;
                if (isColorByLabelActive) {
                  formattedValue = cellStates[idx] ?? DEFAULT_NO_DATA_MESSAGE;
                } else if (value[1] !== null) {
                  formattedValue = formatValue(value[1], unit);
                }
                setTooltipContent({
                  name,
                  timestamp: value[0],
                  value: value[1],
                  formattedValue,
                  status: status.name,
                  statusColor: status.color,
                  rowIdx,
                  labels,
                });
              }}
            />
          );
        })}
      </CellsContainer>
    </RowContainer>
  );
};

interface StatusHistoryTooltipProps {
  tooltipContent?: TooltipContent;
  tooltipLinks?: TooltipLink[];
}

const StatusHistoryTooltip = ({ tooltipContent, tooltipLinks }: StatusHistoryTooltipProps) => {
  const { timeZone } = useTimeZone();
  const timeFormat = useUserTimeFormat();

  if (tooltipContent === undefined) return null;

  const hasLinks = tooltipLinks && tooltipLinks.length > 0;
  const seriesInfo = {
    seriesName: tooltipContent.name,
    seriesIndex: tooltipContent.rowIdx,
    seriesId: tooltipContent.name,
    y: tooltipContent.value ?? 0,
    x: tooltipContent.timestamp,
    markerColor: tooltipContent.statusColor ?? '',
    totalSeries: 1,
  };

  return (
    <>
      <Typography variant="body2" sx={{ position: 'absolute', top: (t) => t.spacing(1), left: (t) => t.spacing(2) }}>
        {tooltipContent.timestamp
          ? getFormattedDateTimeString({
              date: tooltipContent.timestamp,
              timeZone,
              timeFormat,
            })
          : undefined}
      </Typography>
      <Stack
        sx={{ minWidth: PANEL_TOOLTIP_MIN_WIDTH, maxWidth: PANEL_TOOLTIP_MAX_WIDTH, width: 'max-content' }}
        spacing={1}
      >
        {tooltipContent.status !== undefined && (
          <Stack direction="row" spacing={'6px'} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: '12px',
                height: '12px',
                borderRadius: '2px',
              }}
              style={{
                backgroundColor: tooltipContent.statusColor,
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {tooltipContent.status || '(No status)'}
            </Typography>
          </Stack>
        )}

        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography
            variant="body2"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
          >
            {tooltipContent.name}
          </Typography>
          {hasLinks && <SeriesTooltipActions seriesInfo={seriesInfo} links={tooltipLinks} />}
        </Stack>
        <Typography variant="caption">{tooltipContent.formattedValue}</Typography>
      </Stack>
    </>
  );
};

interface StatusHistoryPanelLoadedProps extends StatusHistoryProps {
  queryResults: Array<
    TimeSeriesDataResponse & {
      type: 'TimeSeries';
    } & {
      isFetchingMore?: boolean;
    } & {
      definition?: QueryDefinition;
      error: unknown;
      isFetching: boolean;
      isFetchingMore: boolean;
      isLoading: boolean;
      refetch?: () => void;
    }
  >;
  isFetching: boolean;
}

export const StatusHistoryPanelLoaded = (props: StatusHistoryPanelLoadedProps) => {
  const {
    width,
    height,
    queryResults,
    panelKey,
    thresholds,
    unit,
    visual,
    links,
    onEventClick,
    isFetching,
    color_mode,
    label_grouping,
    syncGroup: syncGroupProp,
  } = props;

  const { enableStatusHistoryColorMode } = useFeatureFlags();
  const isColorByLabelActive = enableStatusHistoryColorMode && color_mode === 'ByLabel';
  const allSeriesFlat = useMemo(() => mergeTimeSeries(queryResults), [queryResults]);
  const { resolvedLabelGrouping: labelGrouping } = useResolvedLabelColorMappings({
    labelGrouping: label_grouping,
    allSeries: allSeriesFlat,
    colorByLabelActive: isColorByLabelActive,
  });
  /** Must match legend: rows/grouping use label colors only when the feature + ByLabel mode are on. */
  const labelBasedColorActive = isColorByLabelActive && Boolean(labelGrouping);
  const { theme } = useTheme();
  const chartsTheme = useChartsTheme();
  const fallbackColor = chartsTheme.thresholds.defaultColor;
  const [tooltipContent, setTooltipContent] = useState<TooltipContent | undefined>();
  const cellContext = useMemo(
    () =>
      tooltipContent
        ? {
            name: tooltipContent.name,
            labels: tooltipContent.labels,
            value: tooltipContent.value,
            timestamp: tooltipContent.timestamp,
          }
        : undefined,
    [tooltipContent]
  );
  const tooltipLinks = useStatusHistoryTooltipLinks(links, cellContext);
  const [hoveredColIdx, setHoveredColIdx] = useState<number | undefined>(undefined);

  const { timeZone } = useTimeZone();
  const timeFormat = useUserTimeFormat();
  const { absoluteTimeRange, setTimeRange } = useTimeRange();
  const eventCtx = useEventContext();

  const sortedSteps = useMemo(() => {
    const configuredSteps = thresholds?.steps ?? [];
    // Sort by highest to lowest threshold
    return [...configuredSteps].sort((a, b) => b.value - a.value);
  }, [thresholds]);

  const getStatus = useCallback(
    (value: number | null) => {
      if (value === null)
        return {
          color: theme.palette.border.default,
          name: '',
        };

      // Find the first threshold that the value matches or exceeds
      const closestValue = sortedSteps.findIndex((step) =>
        step.operator === 'Equal' ? value === step.value : value >= step.value
      );

      return (
        // Use the step with the closest value first or fall back to the base
        sortedSteps[closestValue] ??
        thresholds?.base ?? {
          color: fallbackColor,
          name: '',
        }
      );
    },
    [sortedSteps, thresholds, fallbackColor, theme.palette.border.default]
  );

  const mappingsByValue = useMemo(() => {
    const map = new Map<string, { color: string | undefined; name?: string }>();
    for (const m of labelGrouping?.color_mapping?.mappings ?? []) {
      map.set(m.label_value, { color: m.color, name: m.name });
    }
    return map;
  }, [labelGrouping?.color_mapping?.mappings]);

  const getStatusFromLabel = useCallback(
    (label: string | undefined) => {
      if (label === undefined) {
        return {
          color: theme.palette.border.default,
          name: '',
        };
      }

      const matched = mappingsByValue.get(label);
      if (matched) {
        return {
          color: matched.color ?? labelGrouping?.color_mapping?.base?.color ?? fallbackColor,
          name: matched.name ?? label,
        };
      }

      return {
        color: labelGrouping?.color_mapping?.base?.color ?? fallbackColor,
        name: label,
      };
    },
    [mappingsByValue, labelGrouping?.color_mapping?.base?.color, fallbackColor, theme.palette.border.default]
  );

  const getLegendSwatches = useCallback(() => {
    const swatches: Array<{ name?: string; color?: string }> = [];

    if (isColorByLabelActive && labelGrouping?.color_mapping?.mappings) {
      const baseLabelColor = labelGrouping.color_mapping.base?.color;
      for (const m of labelGrouping.color_mapping.mappings) {
        swatches.push({ name: m.name ?? m.label_value, color: m.color ?? baseLabelColor ?? fallbackColor });
      }
      return swatches;
    }

    const base = thresholds?.base;

    if (base && base.name && base.color) {
      swatches.push(base);
    }

    sortedSteps.forEach((step) => {
      if (step.name && step.color) {
        swatches.push(step);
      }
    });

    return swatches;
  }, [isColorByLabelActive, labelGrouping, thresholds, sortedSteps, fallbackColor]);

  const swatches = getLegendSwatches();

  // Calculate a common time scale using the absolute time range from the hook and step from data
  const timeScale = useMemo(() => {
    const dataTimeScale = getCommonTimeScale(queryResults.map((result) => result.data));
    if (!dataTimeScale) return undefined;

    // Use the user's requested time range (absoluteTimeRange) but keep the step from the data
    return {
      startMs: absoluteTimeRange.start.valueOf(),
      endMs: absoluteTimeRange.end.valueOf(),
      rangeMs: absoluteTimeRange.end.valueOf() - absoluteTimeRange.start.valueOf(),
      stepMs: dataTimeScale.stepMs,
    };
  }, [queryResults, absoluteTimeRange]);

  const series = useMemo(() => {
    if (!timeScale) return;

    // Stack the results of multiple series on top of each other in the graph
    return queryResults.flatMap((queryResult) => {
      return queryResult.data.series.map((s) => {
        // Add in missing null values
        const { values } = getTimeSeriesValues({
          series: s,
          timeScale,
        });

        return {
          ...s,
          values,
        };
      });
    });
  }, [queryResults, timeScale]);

  const statePriority = useMemo(
    () => (labelBasedColorActive ? labelGrouping?.color_mapping?.mappings?.map((m) => m.label_value) : undefined),
    [labelBasedColorActive, labelGrouping?.color_mapping?.mappings]
  );

  const groupedSeries = useGroupedSeries(
    labelBasedColorActive ? series : undefined,
    labelGrouping?.color_by ?? '',
    statePriority
  );

  const displaySeries: Array<SeriesWithLabels & { cellStates?: Array<string | undefined> }> | undefined =
    groupedSeries ?? series;

  const labelWidth = useLabelWidth(displaySeries, width);

  const statusPanelKey = panelKey ?? 'status-history';
  const appMetadata = useAppMetadataContext();
  const resolvedSyncGroup = resolveSyncGroup(syncGroupProp, appMetadata?.app);
  const timeScaleStartMs = timeScale?.startMs;
  const timeScaleStepMs = timeScale?.stepMs;
  const timestampToPixelX = useCallback(
    (ts: number) => {
      if (timeScaleStartMs === undefined || timeScaleStepMs === undefined) return undefined;
      const numCols = displaySeries?.[0]?.values.length ?? 0;
      const chartWidth = width - labelWidth;
      return statusHistoryTimestampToPixelX(ts, timeScaleStartMs, timeScaleStepMs, numCols, chartWidth);
    },
    [timeScaleStartMs, timeScaleStepMs, displaySeries, width, labelWidth]
  );
  const { crosshairPixelX, broadcastTimestamp, crosshairHandlers } = usePanelCrosshair({
    syncGroup: resolvedSyncGroup,
    panelKey: statusPanelKey,
    timestampToPixelX,
  });

  const updateTimeRange = useCallback(
    ({ start, end }: { start: number; end: number }) => {
      // Calculate the actual visualization width (excluding label and spacing)
      const visualizationWidth = width - labelWidth - ROW_SPACING;

      // Guard against invalid width
      if (visualizationWidth <= 0) return;

      // Create a scale to convert pixel positions to timestamps
      const pixelToTime = scaleLinear(
        { min: 0, max: visualizationWidth },
        { min: absoluteTimeRange.start.valueOf(), max: absoluteTimeRange.end.valueOf() }
      );

      const startTime = pixelToTime(Math.min(start, end));
      const endTime = pixelToTime(Math.max(start, end));

      if (!(endTime > startTime)) return;

      // Update the global time range with the selected range
      setTimeRange({
        start: new Date(startTime),
        end: new Date(endTime),
      });
    },
    [absoluteTimeRange, width, labelWidth, setTimeRange]
  );

  const hasNoData = displaySeries === undefined || displaySeries.length === 0;

  if (timeScale === undefined || (!isFetching && hasNoData)) {
    return (
      <Stack sx={{ width, height, alignItems: 'center', justifyContent: 'center' }}>
        <Typography>{DEFAULT_NO_DATA_MESSAGE}</Typography>
      </Stack>
    );
  }

  // Series is undefined while the data is loading
  if (displaySeries === undefined) return null;

  const hasEvents = eventCtx?.events && eventCtx.events.length > 0;
  const showEvents = hasEvents && height >= MIN_CHART_HEIGHT + AXIS_PLUS_LEGEND_HEIGHT + EVENT_TIMELINE_HEIGHT;
  const eventAreaWidth = width - labelWidth;
  const eventRowOffset = showEvents ? EVENT_TIMELINE_HEIGHT : 0;
  const panelKeyValue = panelKey ?? 'new-status-history-panel';

  // If all rows fit at density-based height, expand to fill the space, ignoring user density setting:
  const listHeight = Math.max(height - AXIS_PLUS_LEGEND_HEIGHT - eventRowOffset, 0);
  const density = visual?.density ?? DEFAULT_VISUAL_DENSITY;
  const minRowHeight = 16;
  const maxDensity = 10;
  const invertedDensity = maxDensity - density;
  const densityBasedRowHeight = invertedDensity * invertedDensity + minRowHeight;
  const totalDensityBasedHeight = densityBasedRowHeight * displaySeries.length;
  const shouldFillSpace = totalDensityBasedHeight <= listHeight;
  const rowHeight = shouldFillSpace && displaySeries.length ? listHeight / displaySeries.length : densityBasedRowHeight;

  return (
    <>
      <SelectableOverlay
        width={width}
        height={height - AXIS_PLUS_LEGEND_HEIGHT}
        onSelectRegion={updateTimeRange}
        leftOffset={labelWidth + ROW_SPACING}
      >
        {showEvents && eventCtx?.events && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: labelWidth,
              width: eventAreaWidth,
            }}
          >
            <EventTimeline
              events={eventCtx.events}
              timeRange={absoluteTimeRange}
              width={eventAreaWidth}
              height={EVENT_TIMELINE_HEIGHT}
              onEventClick={onEventClick}
              parentPanelKey={panelKeyValue}
            />
          </Box>
        )}
        <PanelTooltipWrapper
          sx={{ top: eventRowOffset }}
          width={width}
          tooltipConfig={{ enabled: true, enablePinning: true }}
          panelKey={panelKeyValue}
          renderTooltip={() => {
            if (tooltipContent === undefined) return null;

            return <StatusHistoryTooltip tooltipContent={tooltipContent} tooltipLinks={tooltipLinks} />;
          }}
        >
          <Box
            sx={{ width, height: listHeight, position: 'relative' }}
            onMouseEnter={crosshairHandlers.onMouseEnter}
            onMouseLeave={() => {
              setTooltipContent(undefined);
              setHoveredColIdx(undefined);
              crosshairHandlers.onMouseLeave();
            }}
          >
            <Virtuoso
              data={displaySeries}
              itemContent={(idx, s) => {
                const cellStates = labelBasedColorActive ? s.cellStates : undefined;
                return (
                  <StatusHistoryRow
                    values={s.values}
                    getStatus={getStatus}
                    getStatusFromLabel={labelBasedColorActive ? getStatusFromLabel : undefined}
                    cellStates={cellStates}
                    name={s.formattedName ?? s.name}
                    labels={s.labels}
                    setTooltipContent={setTooltipContent}
                    unit={unit}
                    rowIdx={idx}
                    selectedRowIdx={tooltipContent?.rowIdx}
                    setHoveredColIdx={setHoveredColIdx}
                    onCellHover={broadcastTimestamp}
                    height={rowHeight}
                    labelWidth={labelWidth}
                  />
                );
              }}
            />
            {crosshairPixelX !== undefined && (
              <CrosshairLine pixelX={crosshairPixelX + labelWidth} height={listHeight} />
            )}
          </Box>
        </PanelTooltipWrapper>
      </SelectableOverlay>
      <Box sx={{ marginLeft: `${labelWidth}px`, position: 'relative' }}>
        <TimeRangeHoverPointer
          hoveredColIdx={hoveredColIdx}
          numColumns={displaySeries?.[0]?.values.length}
          timestamp={hoveredColIdx !== undefined ? displaySeries?.[0]?.values[hoveredColIdx]?.[0] : undefined}
          width={width}
          labelWidth={labelWidth}
          timeScale={timeScale}
          timeZone={timeZone}
          timeFormat={timeFormat}
          axisTimeRange={absoluteTimeRange}
        />
        <Axis
          startTime={absoluteTimeRange.start.valueOf()}
          endTime={absoluteTimeRange.end.valueOf()}
          width={width - labelWidth - ROW_SPACING}
        />
      </Box>
      <StaticLegend>
        {swatches.map((swatch, idx) => (
          <StaticLegendItem key={idx} swatchColor={swatch.color ?? fallbackColor} label={swatch.name ?? ''} />
        ))}
      </StaticLegend>
    </>
  );
};
